'use client';

import type { ComponentType } from 'react';
import dynamic from 'next/dynamic';
import { parseAsString, useQueryState } from 'nuqs';
import { createElement, Suspense, useEffect, useRef, useState } from 'react';
import Toast from '@/components/ui/Toast/toast';
import { usePathname } from '@/i18n/routing';
import { MODAL_DATA_PARAM, MODAL_HISTORY_STEPS_STATE, MODAL_PARAM, PUSHED_MODAL_HISTORY_STATE } from '@/modals/consts';
import LoginModal from '@/modals/modules/auth/LoginModal';
import RegisterModal from '@/modals/modules/auth/RegisterModal';
import { getModalData } from '@/modals/utils/getModalData';
import { getModalNavigationVersion } from '@/modals/utils/openModal';
import { useAppSelector } from '@/redux/hooks';

// Auth
const BlockAccountReasonModal = dynamic(() => import('@/modals/modules/auth/+BlockAccountReasonModal'), { ssr: false });
const ResetPasswordModal = dynamic(() => import('@/modals/modules/auth/+ResetPasswordModal'), { ssr: false });
const CongratsModal = dynamic(() => import('@/modals/modules/auth/+CongratsModal'), { ssr: false });
const ConfirmLogoutModal = dynamic(() => import('@/modals/modules/auth/+ConfirmLogout'), { ssr: false });

// Balances
const DailyBonusModal = dynamic(() => import('@/modals/modules/global/+DailyBonusInfoModal'), { ssr: false });
const ShopModal = dynamic(() => import('@/modals/modules/balances/+ShopModal'), { ssr: false });
const DepositResultModal = dynamic(() => import('@/modals/modules/balances/+DepositResultModal'), { ssr: false });
const RedeemModal = dynamic(() => import('@/modals/modules/balances/+RedeemModal'), { ssr: false });
const RedeemResultModal = dynamic(() => import('@/modals/modules/balances/+RedeemResultModal'), { ssr: false });
const LimitReachedModal = dynamic(() => import('@/modals/modules/balances/+LimitReached'), { ssr: false });
const SweepCoinInfoModal = dynamic(() => import('@/modals/modules/balances/+SweepCoinInfo'), { ssr: false });
const FreeGoldCoinModal = dynamic(() => import('@/modals/modules/balances/+FreeGoldCoinModal'), { ssr: false });
const OutOfSweepCoinModal = dynamic(() => import('@/modals/modules/balances/+OutOfSweepCoinModal'), { ssr: false });

// Profile
const SecurityModal = dynamic(() => import('@/modals/modules/profile/+SecurityModal'), { ssr: false });
const KycModal = dynamic(() => import('@/modals/modules/profile/+KycModal'), { ssr: false });
const VerificationRequiredModal = dynamic(() => import('@/modals/modules/global/+VerificationRequiredModal'), { ssr: false });
const KycNotSubmittedModal = dynamic(() => import('@/modals/modules/global/+KycNotSubmittedModal'), { ssr: false });
const KycStateModal = dynamic(() => import('@/modals/modules/global/+KycStateModal'), { ssr: false });
const KycDuplicateAccountHintModal = dynamic(() => import('@/modals/modules/global/+KycDuplicateAccountHintModal'), { ssr: false });
const PostRequestModal = dynamic(() => import('@/modals/modules/profile/+PostlRequestModal'), { ssr: false });

// Global
const ScratchCardModal = dynamic(() => import('@/modals/modules/global/+ScratchCardModal'), { ssr: false });
const DailyBonusShareModal = dynamic(() => import('@/modals/modules/global/+DailyBonusShareModal'), { ssr: false });
const DailyBonusInfoOffer = dynamic(() => import('@/modals/modules/global/+DailyBonusInfoOffer'), { ssr: false });
const SupportModal = dynamic(() => import('@/modals/modules/global/+SupportModal'), { ssr: false });
const CardVerifyRequestModal = dynamic(() => import('@/modals/modules/global/+CardVerifyRequestModal'), { ssr: false });
const VPNDetectedModal = dynamic(() => import('@/modals/modules/global/+VPNDetectedModal'), { ssr: false });

// Game
const AddReviewModal = dynamic(() => import('@/modals/modules/game/+AddReviewModal'), { ssr: false });
const SessionLowBalanceModal = dynamic(() => import('@/modals/modules/game/+SessionLowBalanceModal'), { ssr: false });
const DisallowGameByCategoryModal = dynamic(() => import('@/modals/modules/game/+DisallowGameByCategoryModal'), { ssr: false });
// Fairplay
const FairPlayWelcomeModal = dynamic(() => import('@/modals/modules/fairplay/+FairPlayWelcomeModal'), { ssr: false });
const FairPlayInfoModal = dynamic(() => import('@/modals/modules/fairplay/+FairPlayInfoModal'), { ssr: false });
const WinDetailsModal = dynamic(() => import('@/modals/modules/fairplay/+WinDetailsModal'), { ssr: false });
const VerifyWinModal = dynamic(() => import('@/modals/modules/fairplay/+VerifyWinModal'), { ssr: false });
const FairPlayReportModal = dynamic(() => import('@/modals/modules/fairplay/+FairPlayReportModal'), { ssr: false });

// Gamification
const StatusesCardsModal = dynamic(() => import('@/modals/modules/gamification/+StatusesCardsModal'), { ssr: false });
const StatusesLevelUpCardsModal = dynamic(() => import('@/modals/modules/gamification/+StatusesLevelUpCardsModal'), {
  ssr: false,
});
const CongratsStatusAndLevelUpModal = dynamic(() => import('@/modals/modules/gamification/+CongratsStatus&LevelUp'), { ssr: false });
const StatusLevelShareModal = dynamic(() => import('@/modals/modules/gamification/+StatusLevelShareModal'), { ssr: false });
const StatusShareCardModal = dynamic(() => import('@/modals/modules/gamification/+StatusShareCardModal'), { ssr: false });
const LevelShareCardModal = dynamic(() => import('@/modals/modules/gamification/+LevelShareCardModal'), { ssr: false });

export const LOGIN_MODAL = 'login' as const;

const modals = {
  // Auth
  'login': LoginModal,
  'register': RegisterModal,
  'block-account-reason': BlockAccountReasonModal,
  'reset-password': ResetPasswordModal,
  'congratulations': CongratsModal,
  'confirm-logout': ConfirmLogoutModal,

  // Balances
  'deposit': ShopModal,
  'daily-bonus-info-offer': DailyBonusInfoOffer,
  'redeem': RedeemModal,
  'redeem-result': RedeemResultModal,
  'limit-reached': LimitReachedModal,
  'deposit-result': DepositResultModal,
  'sweep-coin-info': SweepCoinInfoModal,
  'free-gold-coin': FreeGoldCoinModal,
  'out-of-sweep-coin': OutOfSweepCoinModal,

  // Profile
  'security': SecurityModal,
  'kyc': KycModal,
  'postal-request-code': PostRequestModal,

  // Global
  'daily-bonus': DailyBonusModal,
  'daily-bonus-share': DailyBonusShareModal,
  'scratch-card': ScratchCardModal,
  'support': SupportModal,
  'verification-required': VerificationRequiredModal,
  'kyc-not-submitted': KycNotSubmittedModal,
  'kyc-state': KycStateModal,
  'kyc-duplicate-account-hint': KycDuplicateAccountHintModal,
  'card-verify-request': CardVerifyRequestModal,
  'vpn-detected': VPNDetectedModal,

  // Game
  'add-review': AddReviewModal,
  'game-session-low-balance': SessionLowBalanceModal,
  'disallow-game-by-category': DisallowGameByCategoryModal,

  // Fairplay
  'fairplay-info': FairPlayInfoModal,
  'fairplay-welcome': FairPlayWelcomeModal,
  'win-details': WinDetailsModal,
  'verify-win': VerifyWinModal,
  'fairplay-report': FairPlayReportModal,

  // Gamification
  'gm-card-statuses': StatusesCardsModal,
  'gm-card-level-up': StatusesLevelUpCardsModal,
  'gm-congratulations-status-level': CongratsStatusAndLevelUpModal,
  'gm-status-share': StatusLevelShareModal,
  'gsc': StatusShareCardModal,
  'glc': LevelShareCardModal,
} as const;

export type ModalNameType = keyof typeof modals;

export type ModalPayload<TName extends ModalNameType>
  = 'modalData' extends keyof React.ComponentProps<(typeof modals)[TName]>
    ? React.ComponentProps<(typeof modals)[TName]>['modalData']
    : void;

const unLoggedModals = [
  'deposit',
  'withdraw',
  'kyc',
  'verification-required',
  'kyc-not-submitted',
  'kyc-state',
  'kyc-duplicate-account-hint',
  'redeem',
  'redeem-result',
  'daily-bonus',
  'limit-reached',
  'free-gold-coin',
  'out-of-sweep-coin',
  'deposit-result',
  'postal-request-code',
  'card-verify-request',
  'add-review',
  'user-profile',
  'scratch-card',
] as const;

const loggedOnlyModals = ['login', 'register', 'congratulations'] as const;

const isPushedModalHistoryEntry = (): boolean => {
  const state = window.history.state as Record<string, unknown> | null;
  return state?.[PUSHED_MODAL_HISTORY_STATE] === true;
};

const getCurrentModalKey = (): string => {
  const currentParams = new URLSearchParams(window.location.search);
  const currentModalParam = currentParams.get(MODAL_PARAM) ?? '';
  const currentModalDataParam = currentParams.get(MODAL_DATA_PARAM);

  return `${currentModalParam}\n${currentModalDataParam ?? ''}`;
};

const closePushedModalHistoryEntry = (steps: number): Promise<void> => {
  return new Promise((resolve) => {
    let timeoutId = 0;

    const finish = () => {
      window.removeEventListener('popstate', finish);
      window.clearTimeout(timeoutId);
      resolve();
    };

    window.addEventListener('popstate', finish, { once: true });
    window.history.go(-steps);
    timeoutId = window.setTimeout(finish, 150);
  });
};

export default function ModalsProvider() {
  const userSession = useAppSelector(s => s.user.userSession);
  const loading = useAppSelector(s => s.user.loading);
  const pathname = usePathname();

  const [modalParam, setModalParam] = useQueryState(MODAL_PARAM, parseAsString.withDefault(''));
  const [modalDataParam, setModalDataParam] = useQueryState(MODAL_DATA_PARAM);
  const [modalActive, setModalActive] = useState<string | null>(null);
  const closingModalKeyRef = useRef<string | null>(null);

  function clearModalData() {
    return Promise.all([
      setModalParam(null),
      setModalDataParam(null),
    ]);
  }

  useEffect(() => {
    closingModalKeyRef.current = null;

    if (!modalParam) {
      setModalActive(null);
      return;
    }

    if (!modals[modalParam as ModalNameType]) {
      Toast({ type: 'error', title: 'The requested modal is not available' });
      setModalActive(null);
      return;
    }

    if (loading) {
      setModalActive(null);
      return;
    }

    if (!userSession && unLoggedModals.includes(modalParam as never)) {
      setModalActive(LOGIN_MODAL);
      return;
    }

    if (userSession && loggedOnlyModals.includes(modalParam as never)) {
      setModalActive(null);
      return;
    }

    setModalActive(modalParam);
  }, [modalDataParam, modalParam, loading, pathname, userSession]);

  const handleCloseModal = async (): Promise<void> => {
    const modalKey = `${modalParam}\n${modalDataParam ?? ''}`;
    const closeStartedAtNavigationVersion = getModalNavigationVersion();

    if (
      getCurrentModalKey() !== modalKey
      || closingModalKeyRef.current === modalKey
    ) {
      return;
    }

    closingModalKeyRef.current = modalKey;
    await Promise.resolve();

    if (
      getCurrentModalKey() !== modalKey
      || getModalNavigationVersion() !== closeStartedAtNavigationVersion
    ) {
      closingModalKeyRef.current = null;
      return;
    }

    const closingPathname = window.location.pathname;
    const pushedModalEntry = isPushedModalHistoryEntry();
    const extraSteps = window.history.state?.[MODAL_HISTORY_STEPS_STATE] ?? 0;
    const steps = extraSteps + (pushedModalEntry ? 1 : 0);
    if (steps > 0 && window.history.length > steps) {
      await closePushedModalHistoryEntry(steps);
      if (pushedModalEntry) {
        return;
      }
      // A directly loaded Shop URL has no underlying page entry. Return to its
      // bundle entry, then remove the modal params without leaving the site.
      if (window.location.pathname !== closingPathname || getModalNavigationVersion() !== closeStartedAtNavigationVersion) {
        return;
      }
    }

    await clearModalData();
  };

  if (!modalActive) {
    return null;
  }

  type ProviderModalProps = {
    onCloseAction: () => Promise<void>;
    modalData: unknown;
  };

  return (
    <>
      {modalActive && modals[modalActive as ModalNameType]
        ? (
            <Suspense>
              {createElement(
                modals[modalActive] as ComponentType<ProviderModalProps>,
                { onCloseAction: handleCloseModal, modalData: getModalData() },
              )}
            </Suspense>
          )
        : null}
    </>
  );
};
